(() => {
  'use strict';
  if (window.KMCartPairView) return;

  function create(scope, {dialogId = 'km-cart-pair-dialog', pageSize = 3} = {}) {
    const views = new WeakMap();
    const pages = new Map();
    const mobileStates = new Map();
    const retries = new WeakSet();
    let stopScroll = () => {};
    let viewRevision = 0;
    let currentTarget = null;
    let currentKey = null;
    const element = (tag, className, content) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (content !== undefined) node.textContent = content;
      return node;
    };
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
    }).format(Number(value) || 0);

    function productCard(product) {
      const card = element('article', 'km-cart-pair__product');
      card.setAttribute('data-km-cart-pair-product', '');
      card.dataset.kmPairHandle = product.handle;
      card.dataset.kmProductId = String(product.product_id || product.id || '');
      card.dataset.kmProductTitle = product.title || '';
      const imageContainer = element('div', 'km-cart-pair__image');
      const imageSource = product.image?.src || product.images?.[0]?.src;
      if (imageSource) {
        let imageUrl;
        try { imageUrl = new URL(imageSource, location.origin); } catch (error) { imageUrl = null; }
        if (imageUrl && ['https:', 'http:'].includes(imageUrl.protocol)) {
          if (imageUrl.hostname.endsWith('.shopify.com')) imageUrl.searchParams.set('width', '480');
          const image = element('img');
          image.src = imageUrl.href;
          image.alt = product.title || '';
          image.width = 240;
          image.height = 240;
          image.loading = 'lazy';
          imageContainer.append(image);
        }
      }
      const title = element('div', 'km-cart-pair__title', product.title || '');
      const footer = element('div', 'km-cart-pair__footer');
      const prices = element('div', 'km-cart-pair__prices');
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find(variant => variant.available !== false && (variant.available === true
        || variant.inventory_policy === 'continue' || variant.inventory_quantity === undefined || variant.inventory_quantity > 0)) || variants[0];
      if (variant?.price != null) {
        prices.append(element('strong', '', money(variant.price)));
        if (Number(variant.compare_at_price) > Number(variant.price)) prices.append(element('s', '', money(variant.compare_at_price)));
      }
      const button = element('button', 'km-cart-pair__open', '+');
      button.type = 'button';
      button.dataset.kmPairOpen = product.handle;
      if (typeof product.notes === 'string' && product.notes.trim()) button.dataset.kmPairNotes = product.notes.trim();
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', dialogId);
      button.setAttribute('aria-label', 'Choose options for ' + (product.title || 'this product'));
      footer.append(prices, button);
      card.append(imageContainer, title, footer);
      return card;
    }

    function pagination(target, view) {
      let controls = target.querySelector('[data-km-pair-pagination]');
      if (!controls) {
        controls = element('div', 'km-cart-pair__pagination');
        controls.setAttribute('data-km-pair-pagination', '');
        for (const [direction, symbol] of [['prev', '\u2039'], ['next', '\u203a']]) {
          const button = element('button', 'km-cart-pair__arrow', symbol);
          button.type = 'button';
          button.setAttribute(`data-km-pair-page-${direction}`, '');
          button.setAttribute('aria-label', `${direction === 'prev' ? 'Previous' : 'Next'} related products`);
          controls.append(button);
        }
        const status = element('span', 'visually-hidden');
        status.setAttribute('data-km-pair-page-status', '');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        target.querySelector('.km-cart-pair-inline__viewport').prepend(controls, status);
      }
      const pageCount = Math.ceil((view.total || 0) / pageSize);
      controls.hidden = pageCount <= 1;
      const busy = view.status === 'pending';
      target.querySelector('[data-km-pair-page-prev]').disabled = busy || view.pageNum <= 1;
      target.querySelector('[data-km-pair-page-next]').disabled = busy || view.pageNum >= pageCount;
      target.querySelector('[data-km-pair-page-status]').textContent = pageCount
        ? `Page ${view.pageNum} of ${pageCount}` : '';
    }

    const onPageClick = event => {
      const button = event.target.closest('[data-km-pair-page-prev], [data-km-pair-page-next]');
      if (!button || button.disabled) return;
      const target = button.closest('[data-km-cart-pair], [data-km-cart-pair-inline]');
      const view = views.get(target);
      if (!view || view.status === 'pending') return;
      event.preventDefault();
      window.KMCartAnalytics?.track('click_cart_recommendation_carousel');
      renderProducts(target, view.handle, view.pageNum + (button.hasAttribute('data-km-pair-page-next') ? 1 : -1));
    };
    scope.addEventListener('click', onPageClick);

    function renderMobile(target, handle) {
      const key = window.KMCartPairData.key(handle, 1, 8);
      if (currentTarget !== target || currentKey !== key) {
        viewRevision++;
        currentTarget = target;
        currentKey = key;
      }
      const retry = retries.delete(target);
      const existing = views.get(target);
      if (!retry && existing?.key === key && existing.revision === viewRevision
        && Date.now() - existing.time < 60000) return;
      stopScroll();
      let state = mobileStates.get(key);
      if (!state || Date.now() - state.time >= 60000) {
        state = {products: [], pageNum: 0, total: 0, scrollLeft: 0, time: Date.now()};
        mobileStates.set(key, state);
        while (mobileStates.size > 24) mobileStates.delete(mobileStates.keys().next().value);
      }
      const list = target.querySelector('[data-km-cart-pair-list]');
      if (!list) return;
      const view = {key, handle, revision: ++viewRevision, status: 'ready', time: Date.now()};
      views.set(target, view);
      target.querySelectorAll('[data-km-pair-list-message]').forEach(node => node.remove());
      const controls = target.querySelector('[data-km-pair-pagination]');
      if (controls) controls.hidden = true;
      list.replaceChildren(...state.products.map(productCard));
      list.scrollLeft = state.scrollLeft;
      target.setAttribute('aria-busy', 'false');
      const showEmpty = () => {
        if (state.products.length || !state.pageNum || state.pageNum * 8 < state.total) return;
        const empty = element('p', 'km-cart-pair__message', 'No related products are available right now.');
        empty.setAttribute('data-km-pair-list-message', '');
        list.after(empty);
      };
      showEmpty();
      let frame = null;
      const isCurrent = () => view.revision === viewRevision && currentTarget === target && target.isConnected
        && window.KMCartPairData.key(handle, 1, 8) === key;
      const schedule = () => {
        if (frame !== null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (!isCurrent() || view.status !== 'ready' || !list.clientWidth) return;
          state.scrollLeft = list.scrollLeft;
          const threshold = (list.querySelector('[data-km-cart-pair-product]')?.offsetWidth || 128) * 2 + 20;
          if (list.scrollWidth - list.clientWidth - list.scrollLeft <= threshold) loadNext();
        });
      };
      let scrollTimer = null;
      let userScroll = false;
      const visibleIndex = () => {
        const first = list.querySelector('[data-km-cart-pair-product]');
        const second = first?.nextElementSibling;
        const stride = second?.offsetLeft - first?.offsetLeft || first?.offsetWidth || 1;
        return Math.round(list.scrollLeft / stride);
      };
      let settledIndex = visibleIndex();
      const onIntent = () => {
        if (!userScroll) settledIndex = visibleIndex();
        userScroll = true;
      };
      const onScroll = () => {
        state.scrollLeft = list.scrollLeft;
        schedule();
        if (!userScroll) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const nextIndex = visibleIndex();
          if (isCurrent() && !target.inert && nextIndex !== settledIndex) {
            window.KMCartAnalytics?.track('click_cart_recommendation_carousel');
          }
          settledIndex = nextIndex;
          userScroll = false;
        }, 180);
      };
      list.addEventListener('scroll', onScroll, {passive: true});
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(name => list.addEventListener(name, onIntent, {passive: true}));
      stopScroll = () => {
        list.removeEventListener('scroll', onScroll);
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(name => list.removeEventListener(name, onIntent));
        clearTimeout(scrollTimer);
        if (frame !== null) cancelAnimationFrame(frame);
      };

      async function loadNext() {
        if (!isCurrent() || view.status !== 'ready' || state.pageNum && state.pageNum * 8 >= state.total) return;
        view.status = 'pending';
        target.setAttribute('aria-busy', 'true');
        const message = element('div', 'km-cart-pair__load-more');
        message.setAttribute('data-km-pair-scroll-message', '');
        const loader = element('span', 'km-cart-pair__loader');
        loader.setAttribute('role', 'status');
        loader.setAttribute('aria-label', 'Loading related products');
        message.append(loader);
        list.append(message);
        try {
          const pageNum = state.pageNum + 1;
          const {products, total} = await window.KMCartPairData.load(handle, pageNum, 8);
          if (!isCurrent()) return;
          const seen = new Set(state.products.map(product => product.handle));
          const additions = products.filter(product => !seen.has(product.handle));
          state.products.push(...additions);
          state.pageNum = pageNum;
          state.total = total;
          state.time = view.time = Date.now();
          message.remove();
          list.append(...additions.map(productCard));
          view.status = 'ready';
          showEmpty();
          schedule();
        } catch (error) {
          if (!isCurrent()) return;
          view.status = 'error';
          const notice = element('p', 'km-cart-pair__message', 'Unable to load related products. ');
          notice.setAttribute('role', 'alert');
          const button = element('button', 'text-link', 'Retry');
          button.type = 'button';
          button.setAttribute('data-km-pair-list-retry', '');
          notice.append(button);
          message.replaceChildren(notice);
        } finally {
          if (view.status !== 'error') message.remove();
          if (views.get(target) === view) target.setAttribute('aria-busy', 'false');
        }
      }

      if (!state.pageNum || retry) loadNext();
      else schedule();
    }

    async function renderProducts(target, handle, requestedPage) {
      if (!window.KMCartPairData || !handle) return;
      if (window.matchMedia('(max-width: 768px)').matches && target.matches('[data-km-cart-pair-inline]')) {
        return renderMobile(target, handle);
      }
      stopScroll();
      const contextKey = window.KMCartPairData.key(handle, 1, pageSize);
      const pageNum = requestedPage || pages.get(contextKey) || 1;
      const key = window.KMCartPairData.key(handle, pageNum, pageSize);
      if (currentTarget !== target || currentKey !== key) {
        viewRevision++;
        currentTarget = target;
        currentKey = key;
      }
      const existing = views.get(target);
      if (existing?.key === key && Date.now() - existing.time < 60000
        && (existing.status !== 'pending' || existing.revision === viewRevision)) return;
      const revision = ++viewRevision;
      pages.set(contextKey, pageNum);
      while (pages.size > 24) pages.delete(pages.keys().next().value);
      const view = {key, revision, handle, pageNum, total: existing?.handle === handle ? existing.total : 0,
        status: 'pending', time: Date.now()};
      views.set(target, view);
      const list = target.querySelector('.km-cart-pair__list, [data-km-cart-pair-list]');
      if (!list) return;
      list.replaceChildren();
      target.querySelectorAll('[data-km-pair-list-message]').forEach(node => node.remove());
      const loader = element('span', 'km-cart-pair__loader');
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-label', 'Loading related products');
      loader.setAttribute('data-km-pair-list-message', '');
      list.after(loader);
      target.setAttribute('aria-busy', 'true');
      const carousel = target.querySelector('km-cart-pair-carousel');
      carousel?.setAttribute('data-km-pair-remote', '');
      pagination(target, view);
      try {
        const {products, total} = await window.KMCartPairData.load(handle, pageNum, pageSize);
        if (revision !== viewRevision || !target.isConnected || window.KMCartPairData.key(handle, pageNum, pageSize) !== key) {
          if (views.get(target) === view) views.delete(target);
          return;
        }
        const lastPage = Math.max(1, Math.ceil(total / pageSize));
        if (pageNum > lastPage) return renderProducts(target, handle, lastPage);
        list.replaceChildren(...products.map(productCard));
        if (!products.length) {
          const emptyTemplate = target.querySelector('[data-km-pair-empty]');
          const message = emptyTemplate
            ? emptyTemplate.content.firstElementChild.cloneNode(true)
            : element('p', 'km-cart-pair__message', 'No related products are available right now.');
          message.setAttribute('data-km-pair-list-message', '');
          list.after(message);
        }
        view.total = total;
        view.status = 'ready';
        list.scrollLeft = 0;
      } catch (error) {
        if (revision !== viewRevision || !target.isConnected || window.KMCartPairData.key(handle, pageNum, pageSize) !== key) {
          if (views.get(target) === view) views.delete(target);
          return;
        }
        view.status = 'error';
        const message = element('p', 'km-cart-pair__message', 'Unable to load related products. ');
        message.setAttribute('data-km-pair-list-message', '');
        message.setAttribute('role', 'alert');
        const retry = element('button', 'text-link', 'Retry');
        retry.type = 'button';
        retry.setAttribute('data-km-pair-list-retry', '');
        message.append(retry);
        list.after(message);
      } finally {
        loader.remove();
        if (views.get(target) === view || !views.has(target)) {
          target.setAttribute('aria-busy', 'false');
          if (views.get(target) === view) pagination(target, view);
        }
      }
    }

    return {
      render: renderProducts,
      reset: target => { retries.add(target); views.delete(target); },
      destroy: () => { viewRevision++; stopScroll(); mobileStates.clear(); scope.removeEventListener('click', onPageClick); },
      cancel: () => { viewRevision++; stopScroll(); currentTarget = null; currentKey = null; }
    };
  }

  window.KMCartPairView = {create};
})();
